import { useEffect, useMemo, useState } from 'react';
import { Schedule, ScheduleQueueItem } from '@/models/schedule';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer } from '@/shared/components/form/MaterialFormElements';
import { useCreateQueueItem, useDeleteQueueItem, useUpdateQueueItem } from '@/services/schedules';
import { extractErrorMessage } from '@/utils/helpers';
import PayloadForm from './payload-form';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    schedule: Schedule;
    existingItem?: ScheduleQueueItem; // present in edit mode
}

const QueueItemModal = ({ isOpen, toggle, schedule, existingItem }: Props) => {
    const { mutateAsync: createItem, isPending: isCreating } = useCreateQueueItem();
    const { mutateAsync: updateItem, isPending: isUpdating } = useUpdateQueueItem();
    const { mutateAsync: deleteItem, isPending: isDeleting } = useDeleteQueueItem();

    // Memoize the initial-payload prop so PayloadForm + its effects don't see
    // a fresh `{}` identity on every parent render — that was an infinite
    // render loop in create mode where the form's onChange→setState→re-render
    // chain kept retriggering itself.
    const initialPayload = useMemo<Record<string, unknown>>(() => existingItem?.payload ?? {}, [existingItem]);

    const [payload, setPayload] = useState<Record<string, unknown>>(initialPayload);
    const [error, setError] = useState<string | null>(null);

    // Reset on modal close (so opening for a new item later doesn't show stale
    // state from the previous edit). The keyed remount on PayloadForm (via the
    // parent) clears the form's own internal state.
    // Sync the editable payload from the source whenever the modal OPENS (or the
    // item changes). `isOpen` is in the deps so re-opening the SAME item re-syncs
    // it from initialPayload — otherwise it stays cleared from the previous close.
    useEffect(() => {
        if (isOpen) setPayload(initialPayload);
    }, [isOpen, initialPayload]);

    useEffect(() => {
        if (!isOpen) setError(null);
    }, [isOpen]);

    const handleSave = async () => {
        if (Object.keys(payload).length === 0) {
            setError('Fill at least one field — empty payloads are rejected by the API.');
            return;
        }
        try {
            // callApi never throws on API errors — it resolves { status:'error' } —
            // so inspect the result instead of closing as if it saved.
            const result = existingItem
                ? await updateItem({ scheduleId: schedule.id, itemId: existingItem.id, payload })
                : await createItem({ scheduleId: schedule.id, payload });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save queue item.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to save queue item:', e);
            setError(extractErrorMessage(e));
        }
    };

    const handleDelete = async () => {
        if (!existingItem) return;
        setError(null);
        try {
            const result = await deleteItem({ scheduleId: schedule.id, itemId: existingItem.id });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete queue item.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete queue item:', e);
            setError(extractErrorMessage(e));
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!existingItem}
            title={`${existingItem ? 'Edit' : 'Add'} Queue Item`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && (
                        <div style={errorBannerStyle}>{error}</div>
                    )}
                    {/* Key forces a fresh component instance per item id AND per
                        open/close transition, so its internal field state
                        (title/body/media/etc.) reloads cleanly when switching items,
                        moving between edit/create, OR re-opening the same item. */}
                    <PayloadForm
                        key={`${existingItem?.id ?? 'new'}-${isOpen ? 'open' : 'closed'}`}
                        targetType={schedule.target.type}
                        initialPayload={initialPayload}
                        onChange={setPayload}
                    />
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isCreating || isUpdating || isDeleting}
        />
    );
};

export default QueueItemModal;
